import { PaperProps } from '@mui/material/Paper';
import MuiPopover from '@mui/material/Popover';
import clsx from 'clsx';
import { forwardRef } from 'react';

import { IPopoverProps } from './types';

const testId = 'of-popover';

// MUI's paper slot type does not admit data-* attributes, though it forwards
// them. Same cast the Badge primitive uses for its own slot.
type PaperSlotProps = PaperProps & { 'data-testid'?: string };

/**
 * A floating panel anchored to a trigger — the primitive for a notification
 * bell panel, an overflow menu with arbitrary content, or any transient surface
 * that is not a list of values (use `Dropdown` for that).
 *
 * Wraps MUI's Popover rather than positioning a div, because the hard parts are
 * the ones MUI already solves: rendering in a portal so the panel is not clipped
 * by an overflow-hidden header, restoring focus to the trigger on close,
 * trapping focus while open, and closing on both click-outside and Escape.
 *
 * Defaults anchor the panel below the trigger and right-aligned to it, which is
 * what a header-mounted control needs — a left-aligned panel would run off the
 * viewport. Callers can override both origins.
 */
const Popover = forwardRef<HTMLDivElement, IPopoverProps>(
  (
    {
      open,
      anchorEl,
      onClose,
      children,
      className,
      id,
      ariaLabel,
      anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
      transformOrigin = { vertical: 'top', horizontal: 'right' },
    },
    ref
  ) => (
    <MuiPopover
      ref={ref}
      id={id}
      // The contract in types.ts: nothing renders without an anchor. MUI would
      // otherwise place the panel at the viewport's top-left corner.
      open={open && anchorEl !== null}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        paper: {
          // Triggers declare aria-haspopup="dialog"; name and role the panel to
          // match, or assistive tech is promised a dialog it never finds.
          role: 'dialog',
          'aria-label': ariaLabel,
          'data-testid': testId,
          className: clsx(
            className,
            // Keep the gap on the trigger's side. MUI sets `top` from the paper's
            // height, so a bottom margin would not move an upward panel; pull it up.
            transformOrigin.vertical === 'bottom' ? '-mt-8' : 'mt-8',
            'overflow-hidden rounded-lg border border-surface-border',
            'bg-surface-card text-on-surface shadow-3'
          ),
        } as PaperSlotProps,
      }}
    >
      {children}
    </MuiPopover>
  )
);

Popover.displayName = 'Popover';

export { testId as PopoverTestId };
export type { IPopoverProps };
export default Popover;
